import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import type { RedeemProfileAddress } from "@/components/coins/redeem-ship-to"
import {
  type OnboardingAddressErrors,
  OnboardingAddressFieldset,
} from "@/components/onboarding/onboarding-address-fieldset"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { creatorRedemptionShippingAddressPath } from "@/routes"

interface RedemptionAddressDrawerProps {
  redemptionId: number
  /** Prefill for the ADD mode, when the creator has a profile address. */
  profileAddress: RedeemProfileAddress | null
  /**
   * OKS-994 — the destination already stored for THIS redemption, or null when
   * there is none. Presence switches the drawer from ADD to CORRECT: it
   * supplies the prefill and selects the copy.
   *
   * ⚠️ REQUIRED, and it must win over `profileAddress` — this is a data-loss
   * guard, not a convenience. `CreatorRedemptionShippingAddresses
   * Controller#update` assigns all six fields on save, so a correction form
   * prefilled from the PROFILE address overwrites the destination the creator
   * actually typed for this reward the moment they press Save without touching
   * a field. Optional-with-a-default would let a caller silently reintroduce
   * exactly that.
   */
  destination: RedemptionDestination | null
}

/** The six stored address fields, as `ships_to` serves them. */
export interface RedemptionDestination {
  recipient_name: string
  street: string
  street_line_2: string | null
  postal_code: string
  city: string
  country: string
}

/**
 * OKS-985 — how a creator supplies the destination for a LEGACY gear
 * redemption: one claimed before addresses were captured at claim time
 * (UX Spec §1 edge, §2.4).
 *
 * # ⚠️ Per-row, never a page banner
 *
 * PRD OQ-3. The ask is about ONE redemption and a creator can owe more than
 * one; a page-level banner cannot say which. Two owed addresses means two
 * rows, two prompts, two independent submits.
 *
 * # ⚠️ Same fieldset, same wire format
 *
 * `OnboardingAddressFieldset` again — `shipping_address[...]` names, dotted
 * `shipping_address.*` error keys. The controller expects exactly that shape
 * and deliberately NOT the `collab_shipping_address[...]` shape its collab
 * sibling uses, because that sibling's FE is a different component.
 *
 * Inertia `<Form>` with plain inputs. No shadcn `Form`, no react-hook-form,
 * no zod — Crew rule.
 *
 * # ⚠️ The drawer stays OPEN on error
 *
 * `preserveState` keeps it mounted across the refusal so field-level messages
 * land under the offending inputs with the creator's typing intact. The
 * server returns 302 (not 303) precisely so the errors bag survives; a
 * `see_other` would close this with nothing shown.
 */
export function RedemptionAddressDrawer({
  redemptionId,
  profileAddress,
  destination,
}: RedemptionAddressDrawerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // OKS-994 — CORRECT mode when a destination is already stored, ADD mode
  // otherwise. The prefill takes the stored destination first; see the
  // `destination` prop note for why the fallback order is load-bearing.
  const correcting = destination !== null
  const prefill = destination ?? profileAddress

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      {/* Plain Button + controlled `open`, not DrawerTrigger: the state is
          already controlled, so a trigger adds nothing. */}
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="redemption-address-drawer-trigger"
      >
        {correcting
          ? t("rewards.mine.edit_address")
          : t("rewards.mine.add_address")}
      </Button>

      <DrawerContent data-testid="redemption-address-drawer">
        <Form
          action={creatorRedemptionShippingAddressPath(redemptionId)}
          method="patch"
          options={{ preserveScroll: true, preserveState: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing, errors }) => (
            <>
              <DrawerHeader className="text-left">
                <DrawerTitle>
                  {t("rewards.mine.address_drawer.title")}
                </DrawerTitle>
                <DrawerDescription>
                  {/* PO's P-3 ruling: the TITLE is true in both modes and
                      stays; the DESCRIPTION is the string that is false when
                      correcting, so only it swaps. */}
                  {correcting
                    ? t("rewards.mine.address_drawer.edit_description")
                    : t("rewards.mine.address_drawer.description")}
                </DrawerDescription>
              </DrawerHeader>

              <div className="min-h-0 flex-1 overflow-y-auto px-4">
                <OnboardingAddressFieldset
                  values={{
                    recipient_name: prefill?.recipient_name ?? "",
                    street: prefill?.street ?? "",
                    street_line_2: prefill?.street_line_2 ?? "",
                    postal_code: prefill?.postal_code ?? "",
                    city: prefill?.city ?? "",
                    country: prefill?.country ?? "",
                  }}
                  errors={errors as OnboardingAddressErrors}
                />
              </div>

              <DrawerFooter className="shrink-0">
                <Button
                  type="submit"
                  disabled={processing}
                  aria-busy={processing}
                  data-testid="redemption-address-drawer-submit"
                >
                  {processing
                    ? t("rewards.mine.address_drawer.saving")
                    : t("rewards.mine.address_drawer.submit")}
                </Button>
              </DrawerFooter>
            </>
          )}
        </Form>
      </DrawerContent>
    </Drawer>
  )
}
